import {
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
  Separator,
  Link,
  LinkBox,
  LinkOverlay,
} from "@chakra-ui/react";
import { Avatar } from "#app/components/ui/avatar.jsx";
import { Button } from "#app/components/ui/button.jsx";
import { Tag } from "#app/components/ui/tag.jsx";
import { redirect, data } from "react-router";
import { useLoaderData, Link as RemixLink } from "react-router";
import { MdArrowBack } from "react-icons/md";
import {
  MarkdownRenderer,
  BlogEmailCapture,
  ImagePlaceholder,
} from "#app/components/blog/index.js";
import { Paper } from "#app/components/Paper.jsx";
import { CallToAction } from "#app/components/CallToAction.jsx";
import { formatLongDate } from "#app/utils/dateFormat.js";
import { useLocale } from "#app/context/LocaleContext.jsx";
import { useEnv } from "#app/hooks/useEnv.js";
import {
  getBlogPost,
  getRelatedPosts,
  getCTAConfig,
} from "#app/utils/blog.server.js";
import { REGION_LABELS } from "#app/utils/blogRegions.js";
import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import { getBlogPostingSchema } from "#app/utils/structuredData.js";
import { jsonForScript } from "#app/utils/jsonForScript.js";
import { handleEmailCapture } from "#app/utils/emailCaptureAction.server.js";

export const meta = ({ loaderData, matches }) => {
  const baseUrl = getBaseUrl(matches);

  if (!loaderData?.post) {
    return getSeoMeta({
      title: "Blog Post Not Found - Schnerp",
      description: "The blog post you are looking for could not be found.",
      image: getDefaultOgImage(baseUrl),
    });
  }

  const { post } = loaderData;
  // Prefer a per-post canonical override when set (frontmatter `canonicalPath`).
  // Used by the Alberta Parks update post to redirect SEO weight onto the
  // evergreen /alberta-parks explainer page.
  const canonicalUrl = post.canonicalPath
    ? `${baseUrl}${post.canonicalPath}`
    : `${baseUrl}/blog/${post.slug}`;
  return getSeoMeta({
    title: blogPageTitle(post.title),
    description: post.excerpt,
    url: canonicalUrl,
    image: post.featuredImage || getDefaultOgImage(baseUrl),
    type: "article",
    publishedTime: post.publishedAt,
    modifiedTime: post.updatedAt,
    tags: post.tags,
  });
};

// Search results cut titles at about 60 characters, so the brand suffix only
// rides along when the whole title still fits; the post's own title (which
// the blog lint caps at 60) front-loads the query and must not be cut.
const BLOG_TITLE_SUFFIX = " - Schnerp Blog";
const SEARCH_TITLE_MAX = 60;
function blogPageTitle(title) {
  return title.length + BLOG_TITLE_SUFFIX.length <= SEARCH_TITLE_MAX
    ? `${title}${BLOG_TITLE_SUFFIX}`
    : title;
}

// Lets feed readers find the blog's RSS feed from any blog page.
export const links = () => [
  {
    rel: "alternate",
    type: "application/rss+xml",
    title: "Schnerp Blog",
    href: "/blog/feed.xml",
  },
];

// Retired posts keep their URL alive with a permanent redirect to the page
// that now tells the story, so links and search results still land somewhere
// true. alberta-parks-update: the /alberta-parks page carries the full account
// of why Schnerp stopped scanning Alberta Parks and how to get alerts there.
const RETIRED_POSTS = {
  "alberta-parks-update": "/alberta-parks",
};

export const loader = async ({ params }) => {
  const { slug } = params;
  if (RETIRED_POSTS[slug]) throw redirect(RETIRED_POSTS[slug], 301);
  const post = await getBlogPost(slug);

  if (!post) {
    throw new Response("Blog post not found", { status: 404 });
  }

  const relatedPosts = await getRelatedPosts(post, 3);
  const ctaConfig = getCTAConfig(post);

  return { post, relatedPosts, ctaConfig };
};

export const action = async ({ request, params }) => {
  const { slug } = params;
  const post = await getBlogPost(slug);
  if (!post?.emailCapture?.agency) {
    return data({ error: "Not configured" }, { status: 400 });
  }

  const formData = await request.formData();
  const intent = formData.get("intent");

  if (intent === "emailCapture") {
    return handleEmailCapture(formData, { agency: post.emailCapture.agency });
  }

  return data({ error: "Unknown intent" }, { status: 400 });
};

/**
 * Parse content into segments of markdown text and component markers.
 * Markers use the format: <!-- component:type -->
 * Returns an array of { type: "markdown", content } or { type: "component", name }.
 */
function parseContentSegments(content) {
  const COMPONENT_RE = /<!-- component:([\w-]+) -->/g;
  const segments = [];
  let lastIndex = 0;
  let match;

  while ((match = COMPONENT_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "markdown",
        content: content
          .slice(lastIndex, match.index)
          .replace(/<!--[\s\S]*?-->/g, ""),
      });
    }
    segments.push({ type: "component", name: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({
      type: "markdown",
      content: content.slice(lastIndex).replace(/<!--[\s\S]*?-->/g, ""),
    });
  }

  return segments;
}

export default function BlogPost() {
  const { post, relatedPosts, ctaConfig } = useLoaderData();
  const locale = useLocale();
  const {
    title,
    publishedAt,
    updatedAt,
    readTime,
    author,
    content,
    region,
    emailCapture,
    featuredImageCredit,
    featuredImageCreditUrl,
  } = post;

  const segments = parseContentSegments(content);

  const backTo = region ? `/blog?region=${region}` : "/blog";
  const regionLabel = region ? REGION_LABELS[region] : null;

  // The root loader's BASE_URL, which the server and the browser both read.
  const baseUrl = useEnv()?.BASE_URL || "https://schnerp.com";

  const blogPostSchema = getBlogPostingSchema({
    title,
    excerpt: post.excerpt,
    publishedAt,
    updatedAt,
    author,
    url: post.canonicalPath
      ? `${baseUrl}${post.canonicalPath}`
      : `${baseUrl}/blog/${post.slug}`,
    image: post.featuredImage,
    baseUrl,
  });

  return (
    <Container maxW="4xl">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonForScript(blogPostSchema) }}
      />
      <VStack gap={6} align="stretch">
        <Box>
          <Button asChild variant="ghost" colorPalette="brand">
            <RemixLink to={backTo}>
              <Icon as={MdArrowBack} />
              Back to Blog
            </RemixLink>
          </Button>
        </Box>

        <Paper>
          {post.featuredImage && (
            <Box mx={-6} mt={-6} mb={6}>
              <Box
                as="img"
                src={post.featuredImage}
                alt={title}
                // The hero is the page's largest paint: fetch it first.
                loading="eager"
                fetchPriority="high"
                decoding="async"
                w="full"
                maxH="400px"
                objectFit="cover"
                roundedTop="xl"
              />
              {featuredImageCredit && (
                <Text
                  fontSize="2xs"
                  color="subtle"
                  px={6}
                  pt={1.5}
                  css={{
                    // Blend the links into the caption: same subtle grey, a
                    // faint underline, only lifting on hover. Attribution
                    // stays present and clickable without pulling the eye.
                    "& a": {
                      color: "inherit",
                      textDecoration: "underline",
                      textDecorationColor: "currentcolor",
                      textUnderlineOffset: "2px",
                      opacity: 0.85,
                    },
                    "& a:hover": { opacity: 1, color: "muted" },
                  }}
                >
                  Photo by{" "}
                  {featuredImageCreditUrl ? (
                    <Link
                      href={featuredImageCreditUrl}
                      target="_blank"
                      rel="nofollow noopener"
                    >
                      {featuredImageCredit}
                    </Link>
                  ) : (
                    featuredImageCredit
                  )}
                  {/* Unsplash's attribution guideline: link the photographer's
                      profile AND link back to Unsplash, both with utm referral.
                      The "on Unsplash" half only shows for Unsplash credits, so
                      UGC/other sources render as a plain "Photo by <name>". */}
                  {featuredImageCreditUrl?.includes("unsplash.com") && (
                    <>
                      {" "}
                      on{" "}
                      <Link
                        href="https://unsplash.com/?utm_source=schnerp&utm_medium=referral"
                        target="_blank"
                        rel="nofollow noopener"
                      >
                        Unsplash
                      </Link>
                    </>
                  )}
                </Text>
              )}
            </Box>
          )}

          <VStack gap={3} align="stretch">
            <HStack color="muted" fontSize="sm" flexWrap="wrap">
              <Text>{formatLongDate(publishedAt, locale)}</Text>
              {updatedAt && updatedAt !== publishedAt && (
                <>
                  <Text>•</Text>
                  <Text>Updated {formatLongDate(updatedAt, locale)}</Text>
                </>
              )}
              <Text>•</Text>
              <Text>{readTime} min read</Text>
              {regionLabel && (
                <>
                  <Text>•</Text>
                  <Tag.Root size="sm" variant="outline" colorPalette="brand">
                    {regionLabel}
                  </Tag.Root>
                </>
              )}
            </HStack>

            <Heading as="h1" size="md" lineHeight="1.3">
              {title}
            </Heading>

            {author && (
              <HStack gap={3} minW="0" flex="1">
                <Avatar size="sm" name={author.name} src={author.avatar} />
                <VStack align="start" gap={0} minW="0">
                  <Link
                    asChild
                    fontWeight="medium"
                    fontSize="sm"
                    color="inherit"
                  >
                    <RemixLink to="/about">{author.name}</RemixLink>
                  </Link>
                  <Text fontSize="xs" color="muted" lineClamp={1}>
                    {author.title}
                  </Text>
                </VStack>
              </HStack>
            )}

            <Separator my={4} />
          </VStack>

          <Box>
            {segments.map((segment, i) => {
              if (segment.type === "markdown") {
                return (
                  <MarkdownRenderer key={i}>{segment.content}</MarkdownRenderer>
                );
              }
              if (segment.name === "email-capture" && emailCapture) {
                return <BlogEmailCapture key={i} config={emailCapture} />;
              }
              return null;
            })}
          </Box>
        </Paper>

        <CallToAction
          heading={ctaConfig.heading}
          body={ctaConfig.body}
          buttonText={ctaConfig.buttonText}
          buttonLink={<RemixLink to={ctaConfig.buttonLink} />}
        />

        {regionLabel && relatedPosts.length >= 2 && (
          <Box>
            <Heading as="h2" size="xs" mt={8} mb={4}>
              More from {regionLabel}
            </Heading>
            <Paper p={0}>
              <VStack gap={0} separator={<Separator />} align="stretch">
                {relatedPosts.map((related) => (
                  <LinkBox
                    key={related.slug}
                    p={4}
                    _hover={{ bg: "bg.subtle" }}
                    _focusWithin={{ bg: "bg.subtle" }}
                    transition="background 0.2s"
                  >
                    <HStack gap={4} align="start">
                      <Box
                        flexShrink={0}
                        w={{ base: "84px", sm: "120px" }}
                        css={{ aspectRatio: "16/9" }}
                        position="relative"
                      >
                        {related.featuredImage ? (
                          <Box
                            as="img"
                            src={related.featuredImage}
                            alt=""
                            // Below the post: React preloads every eager
                            // image from <head>.
                            loading="lazy"
                            position="absolute"
                            inset="0"
                            w="full"
                            h="full"
                            objectFit="cover"
                            rounded="md"
                          />
                        ) : (
                          <ImagePlaceholder
                            position="absolute"
                            inset="0"
                            rounded="md"
                          />
                        )}
                      </Box>
                      <Box minW="0">
                        <Heading as="h3" size="xs" mb={1}>
                          <LinkOverlay asChild>
                            <RemixLink to={`/blog/${related.slug}`}>
                              {related.title}
                            </RemixLink>
                          </LinkOverlay>
                        </Heading>
                        <Text fontSize="sm" color="muted" lineClamp={2}>
                          {related.excerpt}
                        </Text>
                      </Box>
                    </HStack>
                  </LinkBox>
                ))}
              </VStack>
            </Paper>
          </Box>
        )}

        <Box>
          <Button asChild variant="ghost" colorPalette="brand">
            <RemixLink to={backTo}>
              <Icon as={MdArrowBack} />
              Back to Blog
            </RemixLink>
          </Button>
        </Box>
      </VStack>
    </Container>
  );
}
